import { removePushSubscription, savePushSubscription } from './api';

/**
 * Web push, from the browser's side (doc 11 › Delivery architecture).
 *
 * Every step can fail for reasons that aren't errors — an unsupported browser, a denied
 * prompt, an iPhone that hasn't been added to the home screen — so this reports a state
 * rather than throwing, and the UI explains what to do about it.
 */
export type PushState = 'unsupported' | 'needs-install' | 'default' | 'granted' | 'denied';

export function pushState(): PushState {
  if (typeof window === 'undefined') return 'unsupported';
  // iOS only allows push once the app is installed to the home screen (doc 11).
  if (isIos() && !isStandalone()) return 'needs-install';
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return 'unsupported';
  return Notification.permission as PushState;
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    return await navigator.serviceWorker.register('/sw.js');
  } catch {
    // A blocked or unavailable worker just means no push; everything else still works.
    return null;
  }
}

/** Asks, subscribes, and tells the API. Returns the state we ended up in. */
export async function enablePush(vapidPublicKey: string): Promise<PushState> {
  const state = pushState();
  if (state === 'unsupported' || state === 'needs-install' || state === 'denied') return state;

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission as PushState;

  const registration = (await registerServiceWorker()) ?? (await navigator.serviceWorker.ready);
  const existing = await registration.pushManager.getSubscription();
  const subscription =
    existing ??
    (await registration.pushManager.subscribe({
      // Without this, Chrome refuses to subscribe at all.
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    }));

  await savePushSubscription(subscription.toJSON());
  return 'granted';
}

/** Unsubscribes this device, and forgets it server-side so nothing is sent into the void. */
export async function disablePush(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = await registration?.pushManager.getSubscription();
  if (!subscription) return;

  await removePushSubscription(subscription.endpoint).catch(() => undefined);
  await subscription.unsubscribe().catch(() => undefined);
}

export async function hasPushSubscription(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) return false;
  const registration = await navigator.serviceWorker.getRegistration();
  return !!(await registration?.pushManager.getSubscription());
}

/** iOS Safari, including iPadOS pretending to be a Mac. */
function isIos(): boolean {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // The iOS-only flag, which predates the standard one.
    (navigator as { standalone?: boolean }).standalone === true
  );
}

/**
 * VAPID keys travel as base64url; `subscribe` wants bytes. The buffer is built
 * explicitly so the result is an `ArrayBuffer`-backed view, which is what the DOM types
 * (and Chrome) expect.
 */
function urlBase64ToUint8Array(base64: string): BufferSource {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4))
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const raw = atob(padded);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
}
